/**
 * TenantDetailModal 元件
 * 租戶詳情和編輯 Modal
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '../../components/Modal';
import { get, patch, post, del } from '../../api/superAdminClient';
import useSWR from 'swr';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorDisplay } from '../../components/ErrorDisplay';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { zhTW } from 'date-fns/locale';

/**
 * 租戶詳情回應類型
 */
interface TenantDetailResponse {
  success: boolean;
  data: {
    id: string;
    name: string;
    slug: string;
    phone: string | null;
    is_active: number;
    logo_url: string | null;
    favicon_url: string | null;
    cover_image_url: string | null;
    avatar_url: string | null;
    quota_overrides: Record<string, number | boolean> | null;
    ui_config: Record<string, unknown> | null;
    created_at: string;
    updated_at: string;
    [key: string]: unknown;
  };
  timestamp: string;
}

/**
 * 更新租戶表單 Schema
 */
const updateTenantSchema = z.object({
  quotaOverrides: z
    .record(z.string(), z.union([z.number(), z.boolean()]))
    .optional(),
  uiConfig: z.record(z.string(), z.any()).optional(),
  isActive: z.boolean().optional(),
});

type UpdateTenantFormData = z.infer<typeof updateTenantSchema>;

interface TenantDetailModalProps {
  tenantId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

export function TenantDetailModal({
  tenantId,
  isOpen,
  onClose,
  onUpdate,
}: TenantDetailModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 取得租戶詳情
  const { data, error, isLoading, mutate } = useSWR<TenantDetailResponse>(
    tenantId ? `/api/super-admin/tenants/${tenantId}` : null,
    get as any
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<UpdateTenantFormData>({
    resolver: zodResolver(updateTenantSchema),
  });

  // 當數據載入時，重置表單
  useEffect(() => {
    if (data?.data) {
      reset({
        quotaOverrides: (data.data as any).quota_overrides || {},
        uiConfig: (data.data as any).ui_config || {},
        isActive: data.data.is_active === 1,
      });
    }
  }, [data, reset]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (data?.data) {
      reset({
        quotaOverrides: (data.data as any).quota_overrides || {},
        uiConfig: (data.data as any).ui_config || {},
        isActive: data.data.is_active === 1,
      });
    }
  };

  const onSubmit = async (formData: UpdateTenantFormData) => {
    if (!tenantId) return;

    setIsSubmitting(true);
    try {
      await patch(`/api/super-admin/tenants/${tenantId}`, formData);
      toast.success('租戶更新成功');
      setIsEditing(false);
      await mutate();
      onUpdate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '更新失敗');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActivate = async () => {
    if (!tenantId) return;

    try {
      await post(`/api/super-admin/tenants/${tenantId}/activate`);
      toast.success('租戶已啟用');
      await mutate();
      onUpdate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '啟用失敗');
    }
  };

  const handleDeactivate = async () => {
    if (!tenantId) return;

    try {
      await post(`/api/super-admin/tenants/${tenantId}/deactivate`);
      toast.success('租戶已停用');
      await mutate();
      onUpdate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '停用失敗');
    }
  };

  const handleDelete = async () => {
    if (!tenantId) return;

    try {
      await del(`/api/super-admin/tenants/${tenantId}`);
      toast.success('租戶已刪除');
      onClose();
      onUpdate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '刪除失敗');
    }
  };

  const tenant = data?.data;
  const isActive = tenant?.is_active === 1;

  const footer = isEditing ? (
    <>
      <button
        onClick={handleCancel}
        disabled={isSubmitting}
        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
      >
        取消
      </button>
      <button
        onClick={handleSubmit(onSubmit)}
        disabled={isSubmitting}
        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
      >
        {isSubmitting ? '儲存中...' : '儲存'}
      </button>
    </>
  ) : (
    <>
      <button
        onClick={onClose}
        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        關閉
      </button>
      {tenantId && (
        <a
          href={`${import.meta.env.VITE_FRONTEND_URL ?? 'http://localhost:3000'}/admin?tab=tenant-settings&asTenantId=${tenantId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors inline-block text-center"
        >
          商家設定
        </a>
      )}
      <button
        onClick={handleEdit}
        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
      >
        編輯
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="租戶詳情"
      footer={footer}
      size="lg"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <ErrorDisplay error={error} />
      ) : tenant ? (
        <div className="space-y-6">
          {/* 基本資訊 */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              基本資訊
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  名稱
                </label>
                <p className="text-gray-900">{tenant.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slug
                </label>
                <p className="text-gray-900">{tenant.slug}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  電話
                </label>
                <p className="text-gray-900">{tenant.phone || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  狀態
                </label>
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {isActive ? '啟用' : '停用'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  建立時間
                </label>
                <p className="text-gray-900">
                  {format(new Date(tenant.created_at), 'yyyy-MM-dd HH:mm:ss', {
                    locale: zhTW,
                  })}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  更新時間
                </label>
                <p className="text-gray-900">
                  {format(new Date(tenant.updated_at), 'yyyy-MM-dd HH:mm:ss', {
                    locale: zhTW,
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* 編輯表單 */}
          {isEditing && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  啟用狀態
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    {...register('isActive')}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-gray-700">啟用此租戶</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  配額覆蓋 (JSON)
                </label>
                <textarea
                  {...register('quotaOverrides')}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
                  placeholder='{"max_users": 100, "max_products": 500}'
                  defaultValue={JSON.stringify(
                    (tenant as any).quota_overrides || {},
                    null,
                    2
                  )}
                  onChange={e => {
                    try {
                      const value = e.target.value
                        ? JSON.parse(e.target.value)
                        : {};
                      setValue('quotaOverrides', value);
                    } catch {
                      // 忽略 JSON 解析錯誤，讓 zod 驗證處理
                    }
                  }}
                />
                {errors.quotaOverrides && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.quotaOverrides.message?.toString() ||
                      '配額覆蓋格式錯誤'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  UI 設定 (JSON)
                </label>
                <textarea
                  {...register('uiConfig')}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
                  placeholder='{"show_feature_x": true, "theme": "dark"}'
                  defaultValue={JSON.stringify(
                    (tenant as any).ui_config || {},
                    null,
                    2
                  )}
                  onChange={e => {
                    try {
                      const value = e.target.value
                        ? JSON.parse(e.target.value)
                        : {};
                      setValue('uiConfig', value);
                    } catch {
                      // 忽略 JSON 解析錯誤，讓 zod 驗證處理
                    }
                  }}
                />
                {errors.uiConfig && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.uiConfig.message?.toString() || 'UI 設定格式錯誤'}
                  </p>
                )}
              </div>
            </form>
          )}

          {/* 操作按鈕 */}
          {!isEditing && (
            <div className="flex items-center space-x-3 pt-4 border-t">
              {isActive ? (
                <button
                  onClick={handleDeactivate}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                >
                  停用
                </button>
              ) : (
                <button
                  onClick={handleActivate}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  啟用
                </button>
              )}
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                刪除
              </button>
            </div>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
